import React, { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

// Styled Components
import { ButtonContainer, FormContainer, ModalContainerStyled } from './styles';

// Components
import InputText from '../../../inputs/InputText';
import { MainButton } from '../../../inputs/buttons/MainButton';
import RoadRangesFields from '../../../forms/RoadRangesFields';

// Types
import { ModalProps, RoutePresetFormType } from './types';

// Schema
import { routePresetSchema } from './schema';

// GraphQL
import { client } from '../../../../gql/client';
import { evictSurveillanceRoutePresets } from '../../../../gql/cacheHelpers';
import { SAVE_SURVEILLANCE_ROUTE_PRESET } from '../../../../gql/mutations/surveillanceRoutePresets';

// Utils
import { parsePk, pkFormatter } from '../../../../utils/transform';
import { registerPk } from '../../../../utils/pkField';
import {
  showBackendErrorToast,
  showSuccessToast,
} from '../../../../utils/toasts';

/**
 * Alta y edición de una plantilla de recorrido.
 *
 * Mismo patrón que `EditExternalCompanyModal`: el modal lanza su propia
 * mutation y avisa al llamante con `onSuccess` para que revalide.
 *
 * 🔴 Se mandan los rangos de TODAS las carreteras, también cuando solo hay una
 * — al revés que el asistente, que con una sola no manda ninguno porque el
 * rango son los PK del propio recorrido. La plantilla no tiene ningún
 * recorrido donde caer: ella ES el rango.
 */
export const RoutePresetModal = ({
  onClickClose,
  data,
  ...props
}: ModalProps): JSX.Element => {
  const { t } = useTranslation(['pages', 'common', 'forms']);
  const theme = useTheme();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const roads = data?.roads ?? [];
  const preset = data?.preset;

  const {
    handleSubmit,
    register,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm<RoutePresetFormType>({
    defaultValues: {
      name: preset?.name ?? '',
      roadIds: preset?.roadIDs ?? [],
      // La plantilla guarda un rango por carretera, así que la fila sale
      // entera; los extremos de la vía se resuelven del catálogo ya cargado.
      roadRanges: (preset?.roadIDs ?? []).flatMap((roadId) => {
        const road = roads.find((item) => item.id === roadId);
        const range = preset?.roadRanges.find((r) => r.roadId === roadId);
        if (!road || !range) return [];
        return [
          {
            roadId,
            pkInit: pkFormatter(range.pkInitKm, range.pkInitMeter),
            pkEnd: pkFormatter(range.pkEndKm, range.pkEndMeter),
            pkInitRoad: pkFormatter(road.pkInitKm, road.pkInitMeter),
            pkEndRoad: pkFormatter(road.pkEndKm, road.pkEndMeter),
          },
        ];
      }),
    },
    resolver: yupResolver(routePresetSchema) as never,
  });

  const roadIds = watch('roadIds');
  const roadRanges = watch('roadRanges');

  const roadOptions = roads.map((road) => ({
    label: road.name,
    value: road.id,
  }));

  /**
   * Al cambiar la selección se conservan las filas que ya estaban (con sus PK
   * tecleados) y se añaden las nuevas prerrellenadas con los extremos de la
   * vía. Sin red: los extremos ya vienen en el catálogo del loader.
   */
  const handleRoadsChange = useCallback(
    (selectedIds: string[]) => {
      setValue('roadIds', selectedIds, { shouldValidate: true });

      const previous = getValues('roadRanges') ?? [];
      const byRoadId = new Map(previous.map((row) => [row.roadId, row]));

      const rows = selectedIds.flatMap((roadId) => {
        const kept = byRoadId.get(roadId);
        if (kept) return [kept];

        const road = roads.find((item) => item.id === roadId);
        if (!road) return [];

        const boundsInit = pkFormatter(road.pkInitKm, road.pkInitMeter);
        const boundsEnd = pkFormatter(road.pkEndKm, road.pkEndMeter);
        return [
          {
            roadId,
            pkInitRoad: boundsInit,
            pkEndRoad: boundsEnd,
            // Prerrellenados con los extremos; quien configura los ajusta.
            pkInit: boundsInit,
            pkEnd: boundsEnd,
          },
        ];
      });

      setValue('roadRanges', rows, { shouldValidate: true });
    },
    [setValue, getValues, roads]
  );

  const onSubmit = async (values: RoutePresetFormType) => {
    setIsSubmitting(true);
    try {
      await client.mutate({
        mutation: SAVE_SURVEILLANCE_ROUTE_PRESET,
        variables: {
          input: {
            id: preset?.id,
            name: values.name.trim(),
            roadIds: values.roadIds,
            roadRanges: values.roadRanges.flatMap((row) => {
              const init = parsePk(row.pkInit);
              const end = parsePk(row.pkEnd);
              if (!init || !end) return [];
              return [
                {
                  roadId: row.roadId,
                  pkInitKm: init.km,
                  pkInitMeter: init.meter,
                  pkEndKm: end.km,
                  pkEndMeter: end.meter,
                },
              ];
            }),
          },
        },
        refetchQueries: ['GetSurveillanceRoutePresets'],
        awaitRefetchQueries: true,
      });
      // Ver `evictSurveillanceRoutePresets`: Gestión y el asistente consultan
      // variantes distintas de la misma query, y refrescar una no toca la otra.
      evictSurveillanceRoutePresets(client.cache);
      showSuccessToast(t('toasts:success.saved'));
      data?.onSuccess?.();
      onClickClose();
    } catch (error) {
      showBackendErrorToast(
        error,
        t('pages:management.errors.routePresetGeneric')
      );
      setIsSubmitting(false);
    }
  };

  const title = preset
    ? t('pages:management.editRoutePreset')
    : t('pages:management.addRoutePreset');

  return (
    <ModalContainerStyled
      width={60}
      title={title}
      onClickClose={onClickClose}
      fill={theme.colors.black}
    >
      <FormContainer
        {...props}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <InputText
          {...register('name')}
          label={t('pages:surveillance.name')}
          typeInput={'normal'}
          placeholder={t('pages:surveillance.namePlaceholder')}
          borderError={!!errors.name?.message}
          error={errors.name?.message}
        />

        <RoadRangesFields
          roadOptions={roadOptions}
          roadIds={roadIds ?? []}
          roadRanges={roadRanges ?? []}
          onRoadsChange={handleRoadsChange}
          registerPkField={(index, field) =>
            registerPk(register, setValue, `roadRanges.${index}.${field}`)
          }
          errorFor={(index, field) =>
            (
              errors.roadRanges as
                | Array<{
                    pkInit?: { message?: string };
                    pkEnd?: { message?: string };
                  }>
                | undefined
            )?.[index]?.[field]?.message
          }
          roadsError={
            (errors.roadIds as { message?: string } | undefined)?.message
          }
        />

        <ButtonContainer>
          <MainButton
            type="button"
            text={t('common:buttons.cancel')}
            variant="secondary"
            onClick={onClickClose}
            disabled={isSubmitting}
          />
          <MainButton
            type="button"
            text={t('common:buttons.save')}
            variant="primary"
            onClick={() => {
              void handleSubmit(onSubmit)();
            }}
            disabled={isSubmitting}
          />
        </ButtonContainer>
      </FormContainer>
    </ModalContainerStyled>
  );
};
